import { useState, useMemo } from "react";
import { ProgressBar } from "react-bootstrap";
import moment from "moment";
import { MdNextPlan } from "react-icons/md";
import { useParams } from "@tanstack/react-router";
import { IoIosRefreshCircle } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { useClientTable } from "../../components/table/tableFeatures";
import { trellisPalette } from "../../components/custom/analytics/palettes";
import { Button } from "../../components/core/basic/Button";
import { useStartWorkflow } from "../../modules/workflows/api/useStartWokflow";
import { useWorkflows } from "../../modules/workflows/api/useWorkflows";
// oxlint-disable-next-line eslint/no-redeclare -- baseline on new rule introduction
import type { ReportStatus, Workflow } from "../../zodios/apiDefinitions/workflows";
import { useWorkflowState } from "../../modules/workflows/api/useWorkflowState";
import { HStack, VStack, Wrap } from "../../../styled-system/jsx";
import { useWorkflowProgress } from "../../modules/workflows/api/useWorkflowProgress";
import { PageContainer } from "../../components/core/basic/PageContainer";
import { useAppSelector } from "../../redux/hooks";
import { Table } from "../../components/table/Table";
import { createColumnBuilder } from "../../components/table/columnBuilder";
import { usePersistedTableState } from "../../components/table/usePersistedTableState";
import { customFilterFn } from "../../components/table/filterFns";
import { RT8Pagination } from "../../components/table/Pagination";
import { RT8FilterWidget } from "../../modules/merchandising/components/tables/FilterWidget";
import { SearchInput } from "../../components/fields/base/TextField";
import { css } from "../../../styled-system/css";

type WorkflowActionOptions = "sp" | "ads" | "both" | "pricing" | "wm" | "wm-mp" | "shopify";

const columnHelper = createColumnBuilder<Workflow>();

type DownloadListingTask = { task: string; completedAt: string | null };
const downloadTaskColumnHelper = createColumnBuilder<DownloadListingTask>();
const emptyDownloadTasks: DownloadListingTask[] = [];

type TaskRow = {
  name: string;
  job_id: string;
  start_time: string;
  jobStatus?: 0 | 1 | 2 | 3 | 4;
  jobException?: unknown;
};
const taskColumnHelper = createColumnBuilder<TaskRow>();
const emptyTasks: TaskRow[] = [];

const JOB_STATUS = {
  0: "PENDING",
  1: "STARTING",
  2: "SUCCESS",
  3: "FAILURE",
  4: "UNKNOWN_STATUS",
} as const;

const getReportDate = (
  executionOptions: Workflow["task_context"]["pending_reports"][string][number]["pending_report"]["payload"]["execution_options"],
) => {
  if (!executionOptions) return "N/A";
  if (executionOptions.reportDate) return executionOptions.reportDate.replace(/(\d{4})(\d{2})(\d{2})/, "$1-$2-$3");
  if (executionOptions.startDate && executionOptions.endDate)
    return `${executionOptions.startDate} - ${executionOptions.endDate}`;
  if (executionOptions.dataStartTime && executionOptions.dataEndTime)
    return `${executionOptions.dataStartTime} - ${executionOptions.dataEndTime}`;
  return "N/A";
};

type ReportDetailRow = {
  planId: string;
  pending_report: Workflow["task_context"]["pending_reports"][string][number]["pending_report"];
};
const reportDetailColumnHelper = createColumnBuilder<ReportDetailRow>();
const emptyReportDetails: ReportDetailRow[] = [];
type ReportStatusRow = { reportType: string; P: number; S: number; F: number; K: number; E: number; na: number };
const reportStatusColumnHelper = createColumnBuilder<ReportStatusRow>();

const ReportStatus = ({
  pendingReport,
  setReportType,
}: {
  pendingReport: Workflow["task_context"]["pending_reports"];
  setReportType: (reportType: string) => void;
}) => {
  const { t } = useTranslation(["admin"]);
  const data = useMemo(() => {
    const reports: Record<string, { [key in ReportStatus]: number }> = {};
    Object.entries(pendingReport).forEach((plan_reports) => {
      plan_reports[1].forEach((report) => {
        const reportType = report.pending_report.report_type;
        const status = report.pending_report.status;
        if (!reports[reportType]) {
          reports[reportType] = { P: 0, S: 0, F: 0, K: 0, E: 0, "N/A": 0 };
        }
        reports[reportType][status] += 1;
      });
    });
    return Object.entries(reports).map(([reportType, counts]): ReportStatusRow => ({
      reportType,
      P: counts.P,
      S: counts.S,
      F: counts.F,
      K: counts.K,
      E: counts.E,
      na: counts["N/A"],
    }));
  }, [pendingReport]);

  const columns = useMemo(
    () => [
      reportStatusColumnHelper.accessor("reportType", { header: t(($) => $.admin.workflows["Report"]), size: 700 }),
      reportStatusColumnHelper.accessor("P", { header: "P", size: 60, meta: { justify: "center" } }),
      reportStatusColumnHelper.accessor("S", { header: "S", size: 60, meta: { justify: "center" } }),
      reportStatusColumnHelper.accessor("F", { header: "F", size: 60, meta: { justify: "center" } }),
      reportStatusColumnHelper.accessor("K", { header: "K", size: 60, meta: { justify: "center" } }),
      reportStatusColumnHelper.accessor("E", { header: "E", size: 60, meta: { justify: "center" } }),
      reportStatusColumnHelper.accessor("na", { header: "N/A", size: 60, meta: { justify: "center" } }),
      reportStatusColumnHelper.display({
        id: "actions",
        header: "",
        size: 40,
        enableSorting: false,
        cell: (info) => (
          <span style={{ cursor: "pointer" }} onClick={() => setReportType(info.row.original.reportType)}>
            <MdNextPlan />
          </span>
        ),
      }),
    ],
    [setReportType, t],
  );

  const table = useClientTable({
    data,
    columns,
    enableSorting: true,
  });

  return <Table table={table} />;
};

const Workflows = () => {
  const { t } = useTranslation(["admin"]);
  const { workflow_id: workflowIdParam = null } = useParams({ strict: false });
  const [workflowId, setWorkflowId] = useState(workflowIdParam);
  const [reportType, setReportType] = useState<string | null>(null);

  const channel = useAppSelector((state) => state.channel.currentChannel);

  const channelWorkflowActions: Record<string, Array<{ label: string; value: WorkflowActionOptions }>> = {
    amazon: [
      { label: t(($) => $.admin.workflows["Start SP API Workflow"]), value: "sp" },
      { label: t(($) => $.admin.workflows["Start Ads API Workflow"]), value: "ads" },
      { label: t(($) => $.admin.workflows["Start Onboarding (SP API + Ads API) Workflow"]), value: "both" },
      { label: t(($) => $.admin.workflows["Start Pricing Workflow"]), value: "pricing" },
    ],
    walmart: [
      { label: t(($) => $.admin.workflows["Start Ads API Workflow"]), value: "wm" },
      { label: t(($) => $.admin.workflows["Start Marketplace API Workflow"]), value: "wm-mp" },
    ],
    shopify: [{ label: t(($) => $.admin.workflows["Start Shopify Workflow"]), value: "shopify" }],
  };

  const workflowActions = channelWorkflowActions[channel] || [];

  const WORKFLOW_STATUS_OPTIONS = useMemo(
    () => [
      { label: t(($) => $.admin.workflows.status.Pending), value: "PENDING" },
      { label: t(($) => $.admin.workflows.status.Running), value: "RUNNING" },
      { label: t(($) => $.admin.workflows.status.Success), value: "SUCCESS" },
      { label: t(($) => $.admin.workflows.status.Failed), value: "FAILED" },
      { label: t(($) => $.admin.workflows.status.Cancelled), value: "CANCELLED" },
    ],
    [t],
  );

  const startWorkflowMutation = useStartWorkflow({
    onSuccess() {
      alert(t(($) => $.admin.workflows["Workflow started"]));
    },
  });

  const workflowsQuery = useWorkflows({ page_size: 5000 }, { refetchInterval: 600000 });
  const workflow = workflowsQuery.data?.find((d) => d.id === workflowId);

  const columns = useMemo(
    () => [
      columnHelper.accessor("id", {
        header: t(($) => $.admin.workflows["ID"]),
        cell: (info) => (
          <a
            href={`https://api.growthvectors.io/admin/api/workflow/${info.getValue()}/change/`}
            target="_blank"
            rel="noopener noreferrer"
            className={css({
              paddingY: "small",
            })}
          >
            {info.getValue()}
          </a>
        ),
        size: 320,
        meta: { columnFilterOptions: { format: "text" } },
      }),
      columnHelper.accessor("tracking_id", {
        header: t(($) => $.admin.workflows["Tracking ID"]),
        size: 320,
        meta: { columnFilterOptions: { format: "text" } },
      }),
      columnHelper.accessor("status", {
        header: t(($) => $.admin.workflows["Status"]),
        size: 100,
        meta: { justify: "center", columnFilterOptions: { format: "select" } },
      }),
      columnHelper.accessor("created_at", {
        header: t(($) => $.admin.workflows["Created At"]),
        cell: (info) => moment(info.getValue()).format("YYYY-MM-DD HH:mm:ss"),
        size: 180,
        meta: { justify: "center", columnFilterOptions: { format: "date" } },
      }),
      columnHelper.accessor("updated_at", {
        header: t(($) => $.admin.workflows["Updated At"]),
        cell: (info) => moment(info.getValue()).format("YYYY-MM-DD HH:mm:ss"),
        size: 180,
        meta: { justify: "center", columnFilterOptions: { format: "date" } },
      }),
      columnHelper.display({
        id: "actions",
        header: "",
        size: 40,
        enableSorting: false,
        cell: (info) => (
          <span
            style={{ cursor: "pointer" }}
            onClick={() => {
              if (info.row.original.id === workflowId) {
                setWorkflowId(null);
              } else {
                setWorkflowId(info.row.original.id);
              }
              setReportType(null);
            }}
          >
            <MdNextPlan />
          </span>
        ),
      }),
    ],
    [workflowId, t],
  );

  const downloadTaskColumns = useMemo(
    () => [
      downloadTaskColumnHelper.accessor("task", { header: t(($) => $.admin.workflows["Task"]), size: 800 }),
      downloadTaskColumnHelper.accessor("completedAt", {
        header: t(($) => $.admin.workflows["Completed At"]),
        size: 300,
        cell: (info) => {
          const val = info.getValue();
          return val ? moment(val).format("YYYY-MM-DD hh:mm:ss") : "";
        },
        meta: { justify: "center" },
      }),
    ],
    [t],
  );

  const taskColumns = useMemo(
    () => [
      taskColumnHelper.accessor("name", { header: t(($) => $.admin.workflows["Task Name"]), size: 500 }),
      taskColumnHelper.accessor("start_time", {
        header: t(($) => $.admin.workflows["Started At"]),
        size: 240,
        meta: { justify: "center" },
        cell: (info) => (info.getValue() ? moment(info.getValue()).format("YYYY-MM-DD hh:mm:ss") : ""),
      }),
      taskColumnHelper.accessor("job_id", {
        header: t(($) => $.admin.workflows["Job #"]),
        size: 200,
        meta: { justify: "center" },
        sortFn: "alphanumeric",
      }),
      taskColumnHelper.accessor("jobStatus", {
        header: t(($) => $.admin.workflows["State"]),
        size: 200,
        meta: { justify: "center" },
        cell: (info) => {
          const status = info.getValue();
          return status !== undefined ?
              <>
                <span>{JOB_STATUS[status]}</span>
                &nbsp;- (<span>{status}</span>)
              </>
            : null;
        },
      }),
    ],
    [t],
  );

  const reportDetailColumns = useMemo(
    () => [
      reportDetailColumnHelper.accessor("planId", {
        header: t(($) => $.admin.workflows["Plan ID"]),
        meta: { justify: "center" },
        sortFn: "alphanumeric",
        cell: (info) => (
          <a
            href={`https://fawcett.growthvectors.io/admin/backend/plan/${info.getValue()}/change/`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {info.getValue()}
          </a>
        ),
      }),
      reportDetailColumnHelper.accessor((row) => row.pending_report.report_type, {
        id: "report",
        header: t(($) => $.admin.workflows["Report"]),
        cell: (info) => (
          <>
            {info.getValue()}
            <div>
              <small>{info.row.original.pending_report.id}</small>
            </div>
          </>
        ),
      }),
      reportDetailColumnHelper.accessor((row) => row.pending_report.credential, {
        id: "credential",
        header: t(($) => $.admin.workflows["Credential"]),
        meta: { justify: "center" },
        sortFn: "alphanumeric",
        cell: (info) => (
          <a
            href={`https://fawcett.growthvectors.io/admin/backend/credential/${info.getValue()}/change/`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {info.getValue()}
          </a>
        ),
      }),
      reportDetailColumnHelper.accessor((row) => row.pending_report.payload.marketplace_ids, {
        id: "marketplace",
        header: t(($) => $.admin.workflows["Marketplace"]),
        meta: { justify: "center" },
        cell: (info) => (
          <div
            style={{ cursor: "pointer" }}
            onClick={() => {
              alert(info.getValue()?.join("\n"));
            }}
          >
            {info.getValue()?.length}
          </div>
        ),
      }),
      reportDetailColumnHelper.accessor((row) => row.pending_report.payload.execution_options, {
        id: "dates",
        header: t(($) => $.admin.workflows["Dates"]),
        meta: { justify: "center" },
        cell: (info) => getReportDate(info.getValue()),
      }),
      reportDetailColumnHelper.accessor((row) => row.pending_report.status, {
        id: "status",
        header: t(($) => $.admin.workflows["Status"]),
        meta: { justify: "center" },
        cell: (info) => (
          <>
            {info.getValue()}
            <div>
              <small>{info.row.original.pending_report.payload.report_id}</small>
            </div>
          </>
        ),
      }),
      reportDetailColumnHelper.accessor((row) => row.pending_report.wait_until, {
        id: "waitUntil",
        header: t(($) => $.admin.workflows["Wait Until"]),
        meta: { justify: "center" },
        cell: (info) => {
          const pr = info.row.original.pending_report;
          return pr.status !== "S" && pr.wait_until ?
              t(($) => $.admin.workflows["{count} mins"], { count: moment(pr.wait_until).diff(moment(), "minutes") })
            : "n/a";
        },
      }),
      reportDetailColumnHelper.accessor((row) => row.pending_report.warehouse?.id ?? null, {
        id: "warehouseId",
        header: t(($) => $.admin.workflows["Warehouse ID"]),
        meta: { justify: "center" },
        sortFn: "alphanumeric",
        cell: (info) => {
          const val = info.getValue();
          return val ?
              <a
                href={`https://fawcett.growthvectors.io/api/reporting/warehouse/${val}/`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {val}
              </a>
            : "N/A";
        },
      }),
    ],
    [t],
  );

  const { state, setters, initialState } = usePersistedTableState({
    persistKey: "staffWorkflows",
    initialState: {
      sorting: [{ id: "created_at", desc: true }],
      pagination: { pageIndex: 0, pageSize: 25 },
    },
  });

  const workflowsTable = useClientTable({
    data: workflowsQuery.data,
    columns,
    initialState,
    state,
    ...setters,
    defaultColumn: {
      filterFn: customFilterFn,
    },
    meta: {
      columnFilterSelectOptions: {
        status: WORKFLOW_STATUS_OPTIONS,
      },
    },
  });

  const downloadTasksData = useMemo(
    () =>
      workflow ?
        Object.entries(workflow.task_context.download_listings_tasks).map(
          ([task, completedAt]): DownloadListingTask => ({ task, completedAt }),
        )
      : emptyDownloadTasks,
    [workflow],
  );

  const downloadTasksTable = useClientTable({
    data: downloadTasksData,
    columns: downloadTaskColumns,
    enableSorting: true,
  });

  const reportDetailsData = useMemo(() => {
    if (!workflow || !reportType) return emptyReportDetails;
    return Object.entries(workflow.task_context.pending_reports)
      .flatMap(([planId, reports]) =>
        reports
          .filter((r) => r.pending_report.report_type === reportType)
          .map((r): ReportDetailRow => ({ planId, pending_report: r.pending_report })),
      )
      .sort((a, b) => (a.pending_report.wait_until ?? "").localeCompare(b.pending_report.wait_until ?? ""));
  }, [workflow, reportType]);

  const reportDetailsTable = useClientTable({
    data: reportDetailsData,
    columns: reportDetailColumns,
    enableSorting: true,
  });

  const stateQuery = useWorkflowState(workflow?.tracking_id);
  const progressQuery = useWorkflowProgress(workflow?.tracking_id);

  const tasksData = useMemo(() => {
    if (!workflow) return emptyTasks;
    return Object.entries(workflow.task_context.tasks)
      .map(([key, value]): TaskRow => ({
        name: key,
        ...value,
        jobStatus: stateQuery.data?.job_states[value.job_id]?.status,
        jobException: stateQuery.data?.job_states[value.job_id]?.exception,
      }))
      .sort((a, b) => a.start_time.localeCompare(b.start_time));
  }, [workflow, stateQuery.data]);

  const tasksExpanded = useMemo(
    () =>
      tasksData.reduce<Record<string, boolean>>((acc, task, i) => {
        if (task.jobStatus === 3) acc[String(i)] = true;
        return acc;
      }, {}),
    [tasksData],
  );

  const tasksTable = useClientTable({
    data: tasksData,
    columns: taskColumns,
    getRowCanExpand: (row) => row.original.jobStatus === 3,
    state: { expanded: tasksExpanded },
    enableSorting: true,
  });

  return (
    <PageContainer>
      <VStack alignItems={"stretch"}>
        <HStack justifyContent={"space-between"} fontSize={"medium"}>
          <h1 className={css({ fontWeight: "medium", fontSize: "xxlarge" })}>
            {t(($) => $.admin.workflows["Workflows"])}
          </h1>
          <HStack>
            {/* One <span> for the whole run: this HStack has an 8px gap, and splitting the copy
                would add one. */}
            <span>
              {t(($) => $.admin.workflows["Refreshed:"])}{" "}
              <span>
                {workflowsQuery.dataUpdatedAt && !workflowsQuery.isFetching ?
                  moment(workflowsQuery.dataUpdatedAt).format("YYYY-MM-DD HH:mm:ss")
                : workflowsQuery.isFetching ?
                  t(($) => $.admin.workflows["In flight"])
                : t(($) => $.admin.workflows["never"])}
              </span>
            </span>
            <IoIosRefreshCircle
              style={{ cursor: "pointer", color: trellisPalette[0] }}
              onClick={async () => {
                await workflowsQuery.refetch();
              }}
            />
          </HStack>
        </HStack>
        <Wrap>
          {workflowActions.map((action) => (
            <Button
              onPress={() => {
                startWorkflowMutation.mutate(action.value);
              }}
              isPending={startWorkflowMutation.isPending}
              key={action.value}
            >
              {action.label}
            </Button>
          ))}
        </Wrap>

        {(() => {
          switch (workflowsQuery.status) {
            case "pending":
              return <div>{t(($) => $.admin.workflows["Loading..."])}</div>;
            case "error":
              return <div>{t(($) => $.admin.workflows["Error loading workflows"])}</div>;
            case "success":
              return (
                <div>
                  <HStack justifyContent="space-between" alignItems="center" width={"100%"}>
                    <HStack gap="large" alignItems="center">
                      <SearchInput
                        aria-label={t(($) => $.admin.workflows["Search workflows"])}
                        isExpandable
                        css={{ flex: "0 1 350px" }}
                        value={workflowsTable.state.globalFilter}
                        onChange={workflowsTable.setGlobalFilter}
                      />
                      <div className={css({ width: "500px", fontSize: "medium" })}>
                        <RT8FilterWidget table={workflowsTable} />
                      </div>
                    </HStack>
                    <RT8Pagination table={workflowsTable} pageSizeOptions={[10, 25, 50, 100]} />
                  </HStack>
                  <Table table={workflowsTable} />
                </div>
              );
          }
        })()}

        {workflow && (
          <>
            <span className="fs-3">{workflow.id}</span>

            {!!progressQuery.data?.progress && (
              <ProgressBar
                now={(progressQuery.data.progress[0] / progressQuery.data.progress[1]) * 100}
                variant="success"
                style={{ height: "10px" }}
              />
            )}

            <h2 className={css({ fontWeight: "medium", fontSize: "xlarge" })}>
              {t(($) => $.admin.workflows["Tasks"])}
            </h2>
            <Table
              table={tasksTable}
              renderExpander={
                tasksData.some((task) => task.jobStatus === 3) ?
                  (row) => <pre>{row.original.jobException as string}</pre>
                : undefined
              }
            />
            <h2 className={css({ fontWeight: "medium", fontSize: "xlarge" })}>
              {t(($) => $.admin.workflows["Product Download Tasks"])}
            </h2>
            <Table table={downloadTasksTable} />
            <h2 className={css({ fontWeight: "medium", fontSize: "xlarge" })}>
              {t(($) => $.admin.workflows["Reports"])}
            </h2>
            <ReportStatus pendingReport={workflow.task_context.pending_reports} setReportType={setReportType} />
            {reportType && <Table table={reportDetailsTable} />}
          </>
        )}
      </VStack>
    </PageContainer>
  );
};

export default Workflows;
